<template>
  <section class="professional-page">

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <div class="professional-card professional-header-card">

      <div class="card-glow card-glow-blue"></div>
      <div class="card-glow card-glow-purple"></div>

      <div class="header-content">

        <div class="header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M8 7.5H16"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M6 11.5H18"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M8 15.5H16"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <rect
              x="4"
              y="4"
              width="16"
              height="16"
              rx="4"
              stroke="currentColor"
              stroke-width="1.7"
            />
          </svg>
        </div>

        <div class="header-copy">

          <div class="eyebrow">
            Профессиональный профиль
          </div>

          <h2 class="card-title">
            Расскажите о своих работах
          </h2>

          <p class="card-description">
            Добавьте ссылки на профессиональные площадки,
            чтобы работодатели могли быстрее познакомиться
            с вашим опытом и портфолио.
          </p>

        </div>

      </div>

    </div>


    <!-- =====================================================
         LINKS
    ====================================================== -->

    <div class="professional-card links-card">

      <div class="section-heading">

        <div class="section-eyebrow">
          Профили
        </div>

        <h3>
          Ваши профессиональные ссылки
        </h3>

        <p>
          Заполненные профили будут доступны в вашем резюме.
        </p>

      </div>


      <div
        class="profiles-list"
        :class="{
          'profiles-list--saving': isSaving
        }"
      >

        <div
          v-for="item in links"
          :key="item.model"
          class="profile-row"
          :class="{
            filled: !!normalizeValue(form[item.model]),
            focused: focusedField === item.model
          }"
        >

          <div class="platform-icon">
            <img
              :src="item.icon"
              :alt="item.label"
            />
          </div>


          <div class="platform-content">

            <div class="platform-title-row">

              <div class="platform-title">
                {{ item.label }}
              </div>

              <div
                class="status-badge"
                :class="{
                  active:
                    !!normalizeValue(
                      form[item.model]
                    )
                }"
              >
                <span class="status-dot"></span>

                {{
                  normalizeValue(form[item.model])
                    ? "Добавлено"
                    : "Не добавлено"
                }}
              </div>

            </div>


            <div class="platform-description">
              {{ item.description }}
            </div>


            <div class="input-shell">

              <input
                :id="`${item.model}-input`"
                v-model="form[item.model]"
                type="text"
                :placeholder="item.placeholder"
                :disabled="isSaving"
                autocomplete="off"
                spellcheck="false"
                @focus="focusedField = item.model"
                @blur="focusedField = null"
                @keydown.enter.prevent="handleSave"
              />


              <div
                v-if="form[item.model]"
                class="connected-mark"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 10.2L8.1 13.3L15 6.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

            </div>

          </div>

        </div>

      </div>


      <!-- =====================================================
           SAVE
      ====================================================== -->

      <div class="save-area">

        <div class="save-state">
          <span
            class="save-state-dot"
            :class="{ changed: isChanged }"
          ></span>

          {{
            isChanged
              ? "Есть несохранённые изменения"
              : "Все изменения сохранены"
          }}
        </div>


        <button
          class="save-btn"
          :class="{ active: isChanged && !isSaving }"
          :disabled="!isChanged || isSaving"
          type="button"
          @click="handleSave"
        >

          <template v-if="isSaving">
            <span class="button-spinner"></span>
            <span>Сохраняем…</span>
          </template>

          <template v-else>
            Сохранить
          </template>

        </button>

      </div>

    </div>

  </section>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";

import SummaryService
  from "../../api/SummaryService";

import githubIcon
  from "../../assets/github-profile.png";

import dribbbleIcon
  from "../../assets/dribbble-profile.png";

import behanceIcon
  from "../../assets/behance-profile.png";


const props = defineProps({

  initialLinks: {
    type: Object,
    default: () => ({
      githubID: "",
      dribbbleID: "",
      behanceID: ""
    })
  },

  userId: {
    type: String,
    required: true
  },

  snackbar: {
    type: Function,
    default: null
  }

});


const emit = defineEmits([
  "update"
]);


const isSaving =
  ref(false);

const focusedField =
  ref(null);


const links = [

  {
    label: "GitHub",
    model: "github",
    icon: githubIcon,
    description:
      "Репозитории, pet-проекты и ваш вклад в разработку",
    placeholder:
      "github.com/username"
  },

  {
    label: "Dribbble",
    model: "dribbble",
    icon: dribbbleIcon,
    description:
      "Дизайн-концепции, интерфейсы и визуальные работы",
    placeholder:
      "dribbble.com/username"
  },

  {
    label: "Behance",
    model: "behance",
    icon: behanceIcon,
    description:
      "Портфолио, кейсы и завершённые проекты",
    placeholder:
      "behance.net/username"
  }

];


const form = ref(
  createFormFromInitial(
    props.initialLinks
  )
);


const initialForm = ref(
  JSON.stringify(
    normalizeForm(form.value)
  )
);


/* ============================================================
   CHANGES
============================================================ */

const isChanged =
  computed(() => {

    return (
      JSON.stringify(
        normalizeForm(
          form.value
        )
      )
      !==
      initialForm.value
    );

  });


/* ============================================================
   WATCH
============================================================ */

watch(
  () => props.initialLinks,

  value => {

    if (isSaving.value) {
      return;
    }


    form.value =
      createFormFromInitial(
        value
      );


    initialForm.value =
      JSON.stringify(
        normalizeForm(
          form.value
        )
      );

  },

  {
    deep: true
  }
);


/* ============================================================
   HELPERS
============================================================ */

function createFormFromInitial(
  initialLinks = {}
) {

  return {

    github:
      initialLinks.githubID || "",

    dribbble:
      initialLinks.dribbbleID || "",

    behance:
      initialLinks.behanceID || ""

  };

}


function normalizeValue(value) {

  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();

}


function normalizeForm(value) {

  return {

    github:
      normalizeValue(
        value.github
      ),

    dribbble:
      normalizeValue(
        value.dribbble
      ),

    behance:
      normalizeValue(
        value.behance
      )

  };

}


function showSnackbar(
  message,
  type = "success"
) {

  if (!props.snackbar) {
    return;
  }


  props.snackbar(
    message,
    type
  );

}


/* ============================================================
   SAVE
============================================================ */

async function handleSave() {

  if (
    !isChanged.value
    || isSaving.value
  ) {
    return;
  }


  if (!props.userId) {

    showSnackbar(
      "Не удалось определить пользователя",
      "error"
    );

    return;
  }


  const normalized =
    normalizeForm(
      form.value
    );


  const payload = {

    githubID:
      normalized.github,

    githubEnabled:
      Boolean(
        normalized.github
      ),

    dribbbleID:
      normalized.dribbble,

    dribbbleEnabled:
      Boolean(
        normalized.dribbble
      ),

    behanceID:
      normalized.behance,

    behanceEnabled:
      Boolean(
        normalized.behance
      )

  };


  isSaving.value =
    true;


  try {

    const response =
      await SummaryService.updateWorkingProfiles(
        props.userId,
        payload
      );


    form.value = {

      github:
        normalized.github,

      dribbble:
        normalized.dribbble,

      behance:
        normalized.behance

    };


    initialForm.value =
      JSON.stringify(
        normalizeForm(
          form.value
        )
      );


    emit(
      "update",
      {
        ...payload,
        response
      }
    );


    showSnackbar(
      "Профессиональные профили обновлены",
      "success"
    );


    console.log(
      "✅ Профессиональные профили обновлены:",
      payload
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при обновлении профессиональных профилей:",
      error
    );


    const message =
      error?.response?.data?.message
      || error?.response?.data?.error
      || error?.message
      || "Не удалось сохранить изменения";


    showSnackbar(
      message,
      "error"
    );


  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.professional-page {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   BASE CARD
============================================================ */

.professional-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.09
    );

  border-radius: 24px;

  box-shadow:
    0 16px 45px
    rgba(
      15,
      23,
      42,
      0.055
    );
}


/* ============================================================
   HEADER CARD
============================================================ */

.professional-header-card {
  padding: 28px;
}


.header-content {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;

  gap: 15px;
}


.header-icon {
  width: 48px;
  height: 48px;

  flex:
    0
    0
    48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #407edc;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(124, 92, 231, 0.075)
    );

  border:
    1px solid
    rgba(
      40,
      120,
      230,
      0.11
    );

  border-radius: 15px;
}


.header-icon svg {
  width: 23px;
  height: 23px;
}


.header-copy {
  min-width: 0;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #9499a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.card-title {
  margin: 0;

  color: #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  line-height: 1.3;

  letter-spacing: -0.25px;
}


.card-description {
  max-width: 560px;

  margin:
    6px
    0
    0;

  color: #80858e;

  font-size: 11px;

  line-height: 1.5;
}


/* ============================================================
   GLOW
============================================================ */

.card-glow {
  position: absolute;

  width: 220px;
  height: 220px;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(75px);

  opacity: 0.1;
}


.card-glow-blue {
  top: -120px;
  right: -60px;

  background: #2887f7;
}


.card-glow-purple {
  bottom: -160px;
  left: -90px;

  background: #7c5ce7;
}


/* ============================================================
   LINKS CARD
============================================================ */

.links-card {
  padding:
    26px
    20px
    20px;
}


.section-heading {
  padding:
    0
    8px
    18px;
}


.section-heading h3 {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.section-heading p {
  margin:
    5px
    0
    0;

  color: #989da5;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   PROFILE LIST
============================================================ */

.profiles-list {
  display: flex;
  flex-direction: column;

  transition:
    opacity
    0.2s ease;
}


.profiles-list--saving {
  opacity: 0.55;

  pointer-events: none;
}


.profile-row {
  display: grid;

  grid-template-columns:
    44px
    minmax(0, 1fr);

  gap: 13px;

  padding:
    15px
    10px;

  border-radius: 15px;

  transition:
    background-color
    0.2s ease;
}


.profile-row + .profile-row {
  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.06
    );

  border-radius: 0;
}


.profile-row:hover,
.profile-row.focused {
  background:
    rgba(
      40,
      135,
      247,
      0.032
    );
}


/* ============================================================
   PLATFORM ICON
============================================================ */

.platform-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    #f5f7fa;

  border:
    1px solid
    rgba(
      30,
      45,
      65,
      0.055
    );

  border-radius: 13px;
}


.platform-icon img {
  width: 21px;
  height: 21px;

  display: block;

  object-fit: contain;

  opacity: 0.58;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease;
}


.profile-row.filled
.platform-icon img,
.profile-row.focused
.platform-icon img {
  opacity: 1;
}


.profile-row:hover
.platform-icon img {
  transform:
    scale(1.05);
}


/* ============================================================
   COPY
============================================================ */

.platform-content {
  min-width: 0;
}


.platform-title-row {
  display: flex;
  align-items: center;

  gap: 8px;

  min-width: 0;
}


.platform-title {
  color: #383d45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  line-height: 1.3;
}


.platform-description {
  margin-top: 4px;

  color: #999ea6;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   STATUS
============================================================ */

.status-badge {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  min-height: 21px;

  padding:
    0
    7px;

  color: #9a9ea5;

  background:
    rgba(
      30,
      40,
      55,
      0.04
    );

  border:
    1px solid
    rgba(
      30,
      40,
      55,
      0.055
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;
}


.status-dot {
  width: 4px;
  height: 4px;

  background: #b2b5ba;

  border-radius: 50%;
}


.status-badge.active {
  color: #278457;

  background:
    rgba(
      52,
      181,
      112,
      0.075
    );

  border-color:
    rgba(
      52,
      181,
      112,
      0.11
    );
}


.status-badge.active
.status-dot {
  background: #39b874;
}


/* ============================================================
   INPUT
============================================================ */

.input-shell {
  width: 100%;
  min-height: 43px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  margin-top: 10px;

  padding:
    0
    9px
    0
    13px;

  background: #f7f8fa;

  border:
    1px solid
    #e3e6ea;

  border-radius: 12px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.profile-row.focused
.input-shell {
  background: #ffffff;

  border-color:
    #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      92,
      143,
      247,
      0.1
    );
}


.input-shell input {
  min-width: 0;

  flex: 1;

  padding: 0;

  color: #30353d;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.input-shell input::placeholder {
  color: #aaafb7;
}


.connected-mark {
  width: 27px;
  height: 27px;

  flex:
    0
    0
    27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #3ba46d;

  background:
    rgba(
      56,
      175,
      111,
      0.075
    );

  border-radius: 8px;
}


.connected-mark svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   SAVE
============================================================ */

.save-area {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-top: 12px;

  padding:
    18px
    8px
    2px;

  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.065
    );
}


.save-state {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  color: #9a9fa7;

  font-size: 8px;
}


.save-state-dot {
  width: 5px;
  height: 5px;

  background: #b9bdc3;

  border-radius: 50%;
}


.save-state-dot.changed {
  background: #f2a23a;
}


/* ============================================================
   SAVE BUTTON
============================================================ */

.save-btn {
  min-width: 128px;
  min-height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0
    7px
    17px
    rgba(
      40,
      111,
      225,
      0.2
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;

  opacity: 0.4;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease,
    box-shadow
    0.2s ease;
}


.save-btn.active {
  opacity: 1;
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    22px
    rgba(
      40,
      111,
      225,
      0.27
    );
}


.save-btn:disabled {
  cursor: default;

  box-shadow: none;
}


.button-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color: #ffffff;

  border-radius: 50%;

  animation:
    buttonSpin
    0.65s
    linear
    infinite;
}


@keyframes buttonSpin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.professional-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    18px
    50px
    rgba(
      0,
      0,
      0,
      0.24
    );
}


html[data-theme="dark"]
.card-title,
html[data-theme="dark"]
.section-heading h3,
html[data-theme="dark"]
.platform-title {
  color: #f0f1f3;
}


html[data-theme="dark"]
.card-description,
html[data-theme="dark"]
.section-heading p,
html[data-theme="dark"]
.platform-description {
  color: #8d929b;
}


html[data-theme="dark"]
.profile-row + .profile-row {
  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.profile-row:hover,
html[data-theme="dark"]
.profile-row.focused {
  background:
    rgba(
      255,
      255,
      255,
      0.03
    );
}


html[data-theme="dark"]
.platform-icon {
  background:
    rgba(
      255,
      255,
      255,
      0.045
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.input-shell {
  background: #292b30;

  border-color: #3b3e45;
}


html[data-theme="dark"]
.profile-row.focused
.input-shell {
  background: #2e3036;

  border-color: #588bec;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      88,
      139,
      236,
      0.13
    );
}


html[data-theme="dark"]
.input-shell input {
  color: #f0f1f3;
}


html[data-theme="dark"]
.input-shell input::placeholder {
  color: #727780;
}


html[data-theme="dark"]
.save-area {
  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .professional-page {
    gap: 13px;
  }


  .professional-header-card {
    padding: 20px;

    border-radius: 20px;
  }


  .links-card {
    padding:
      21px
      12px
      14px;

    border-radius: 20px;
  }


  .profile-row {
    grid-template-columns:
      40px
      minmax(0, 1fr);

    gap: 10px;

    padding:
      14px
      8px;
  }


  .platform-icon {
    width: 40px;
    height: 40px;
  }


  .status-badge {
    display: none;
  }


  .save-area {
    align-items: stretch;
    flex-direction: column;

    gap: 12px;
  }


  .save-btn {
    width: 100%;

    min-height: 42px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .profile-row,
  .platform-icon img,
  .input-shell,
  .save-btn {
    transition: none;
  }


  .button-spinner {
    animation: none;
  }

}

</style>